import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const LANGUAGES = ['English', 'Hindi', 'Bengali', 'Tamil', 'Telugu', 'Marathi', 'Gujarati', 'Kannada', 'Malayalam', 'Punjabi', 'Urdu'];
const INDIAN_STATES = ['Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Jammu and Kashmir', 'Ladakh', 'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Lakshadweep', 'Puducherry'];

export default function Step1Personal({ data = {}, onSave, saving }) {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    gender: '',
    date_of_birth: '',
    languages: [],
    bio: '',
    primary_mobile: '',
    alternate_mobile: '',
    email: '',
    address_line1: '',
    country: 'India',
    state: '',
    city: '',
    pin_code: '',
    google_maps_link: '',
    ...data,
  });

  useEffect(() => { if (data) setForm(prev => ({ ...prev, ...data })); }, [data]);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleLang = (lang) => update('languages', form.languages.includes(lang) ? form.languages.filter(l => l !== lang) : [...form.languages, lang]);

  const handleSubmit = (e) => { e.preventDefault(); onSave(form); };

  return (
    <form id="step-form-1" onSubmit={handleSubmit} className="p-6 lg:p-8 space-y-6">
      {/* Name */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>First Name *</Label>
          <Input required value={form.first_name} onChange={(e) => update('first_name', e.target.value)} className="mt-1.5" placeholder="Amar" data-testid="p1-first-name" />
        </div>
        <div>
          <Label>Last Name *</Label>
          <Input required value={form.last_name} onChange={(e) => update('last_name', e.target.value)} className="mt-1.5" placeholder="Kumar" data-testid="p1-last-name" />
        </div>
      </div>

      {/* Gender + DOB */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Gender *</Label>
          <Select value={form.gender} onValueChange={(v) => update('gender', v)}>
            <SelectTrigger className="mt-1.5" data-testid="p1-gender"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Date of Birth</Label>
          <Input type="date" value={form.date_of_birth} onChange={(e) => update('date_of_birth', e.target.value)} className="mt-1.5" data-testid="p1-dob" />
        </div>
      </div>

      {/* Languages */}
      <div>
        <Label>Languages you speak</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {LANGUAGES.map((lang) => (
            <button
              type="button"
              key={lang}
              onClick={() => toggleLang(lang)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                form.languages.includes(lang) ? 'bg-[#3B1C73] text-white border-[#3B1C73]' : 'bg-white text-slate-700 border-slate-200 hover:border-[#3B1C73]'
              }`}
            >
              {lang}
            </button>
          ))}
        </div>
      </div>

      {/* Bio */}
      <div>
        <Label>Short Biography</Label>
        <Textarea rows={3} value={form.bio} onChange={(e) => update('bio', e.target.value)} placeholder="A brief intro visible to patients" className="mt-1.5" data-testid="p1-bio" />
      </div>

      {/* Contact */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Primary Mobile *</Label>
          <Input required value={form.primary_mobile} disabled className="mt-1.5 bg-slate-50" data-testid="p1-mobile" />
        </div>
        <div>
          <Label>Alternate Mobile</Label>
          <Input type="tel" value={form.alternate_mobile} onChange={(e) => update('alternate_mobile', e.target.value)} className="mt-1.5" placeholder="+91 9876543210" data-testid="p1-alt-mobile" />
        </div>
      </div>
      <div>
        <Label>Email</Label>
        <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="mt-1.5" placeholder="doctor@example.com" data-testid="p1-email" />
      </div>

      {/* Address */}
      <div className="pt-4 border-t border-slate-100">
        <h3 className="font-semibold text-slate-900 mb-3">Address</h3>
        <div className="space-y-4">
          <div>
            <Label>Address Line 1 *</Label>
            <Input required value={form.address_line1} onChange={(e) => update('address_line1', e.target.value)} placeholder="House / Building, Street" className="mt-1.5" data-testid="p1-address" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label>Country</Label>
              <Input value={form.country} disabled className="mt-1.5 bg-slate-50" data-testid="p1-country" />
            </div>
            <div>
              <Label>State *</Label>
              <Select value={form.state} onValueChange={(v) => update('state', v)}>
                <SelectTrigger className="mt-1.5" data-testid="p1-state"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{INDIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>City *</Label>
              <Input required value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="e.g. Gurugram" className="mt-1.5" data-testid="p1-city" />
            </div>
            <div>
              <Label>PIN Code *</Label>
              <Input required maxLength={6} value={form.pin_code} onChange={(e) => update('pin_code', e.target.value.replace(/\D/g, ''))} placeholder="122001" className="mt-1.5" data-testid="p1-pin" />
            </div>
          </div>
          <div>
            <Label>Google Maps Link (optional)</Label>
            <Input value={form.google_maps_link} onChange={(e) => update('google_maps_link', e.target.value)} placeholder="https://maps.google.com/?q=..." className="mt-1.5" data-testid="p1-maps" />
            <p className="text-xs text-slate-500 mt-1">Open Google Maps, share your location, and paste the link here.</p>
          </div>
        </div>
      </div>
    </form>
  );
}
